import api from './api';
import { LoginRequest, User, TokenPair, MfaChallenge, TotpSetup } from '../types';

/** /auth/login returns one of two shapes; this narrows them safely. */
function isMfaChallenge(data: unknown): data is MfaChallenge {
  return !!data && typeof data === 'object' && 'mfa_token' in (data as Record<string, unknown>);
}

export const authService = {
  /**
   * Log in. Resolves to either a token pair or an MFA challenge — never a token
   * pair with undefined fields.
   */
  async login(credentials: LoginRequest): Promise<TokenPair | MfaChallenge> {
    const response = await api.post<TokenPair | MfaChallenge>(
      '/auth/login',
      credentials,
      { timeout: 60000 }
    );
    const data = response.data;
    if (isMfaChallenge(data)) {
      return data;
    }
    return { access_token: data.access_token, refresh_token: data.refresh_token };
  },

  /** Exchange the login challenge plus a 6-digit authenticator code for tokens. */
  async verifyMfa(mfaToken: string, code: string): Promise<TokenPair> {
    const response = await api.post<TokenPair>(
      '/auth/2fa/verify',
      { mfa_token: mfaToken, code },
      { timeout: 60000 }
    );
    return { access_token: response.data.access_token, refresh_token: response.data.refresh_token };
  },

  /** Same, using one of the one-time backup codes instead of the authenticator. */
  async recoverMfa(mfaToken: string, backupCode: string): Promise<TokenPair> {
    const response = await api.post<TokenPair>(
      '/auth/2fa/recovery',
      { mfa_token: mfaToken, backup_code: backupCode },
      { timeout: 60000 }
    );
    return { access_token: response.data.access_token, refresh_token: response.data.refresh_token };
  },

  /**
   * Begin enrolment. Returns the otpauth URI and backup codes.
   * Calling this REGENERATES the secret and invalidates any previously issued
   * backup codes, so it must be a deliberate user action, never an effect on mount.
   */
  async setupTotp(): Promise<TotpSetup> {
    const response = await api.post<TotpSetup>('/auth/2fa/setup', {}, { timeout: 60000 });
    return response.data;
  },

  /** Activate 2FA with the first code from the authenticator. */
  async confirmTotp(code: string): Promise<void> {
    await api.post('/auth/2fa/confirm', { code }, { timeout: 60000 });
  },

  async logout(): Promise<void> {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  },

  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
};
